import React from 'react';
import { EnterpriseFsatResult } from '@types';
import { Section, Disclaimer } from './ReportShared';

interface EnterpriseFsatResultDisplayProps {
    result: EnterpriseFsatResult;
}

const EnterpriseFsatResultDisplay: React.FC<EnterpriseFsatResultDisplayProps> = ({ result }) => {
    return (
        <div className="space-y-8 animate-fade-in">
            <div className="flex items-start gap-6 bg-slate-50 p-6 rounded-xl border border-slate-200">
                <div className="flex-1">
                    <h3 className="font-bold text-slate-800 text-sm uppercase">Global Risk & Resiliency Summary</h3>
                    <p className="text-sm text-slate-600 leading-relaxed mt-1">{result.overallEnterpriseHealth}</p>
                </div>
                <div className="flex flex-col items-end gap-2 min-w-[180px]">
                    <div className="px-4 py-2 rounded text-sm font-bold uppercase border text-center w-full bg-blue-100 text-blue-800 border-blue-300">
                        Assets Scanned: {result.totalAssetsScanned}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Section title="Systemic Vulnerabilities">
                    <div className="space-y-4">
                        {result.systemicVulnerabilities && result.systemicVulnerabilities.map((vuln, i) => (
                            <div key={i} className="bg-white p-4 rounded-lg border shadow-sm border-l-4 border-l-red-500">
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="font-bold text-slate-800 text-sm">{vuln.category}</h4>
                                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-bold rounded uppercase">{vuln.riskLevel}</span>
                                </div>
                                <p className="text-sm text-slate-600 mb-2">{vuln.description}</p>
                                <div className="text-xs text-slate-500">
                                    <span className="font-semibold text-slate-700">Impacted:</span> {vuln.impactedAssets.join(', ')}
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>
                <Section title="Business Resiliency Alignments">
                    <div className="space-y-4">
                        {result.businessResiliencyAlignments && result.businessResiliencyAlignments.map((align, i) => (
                            <div key={i} className="bg-white p-4 rounded-lg border shadow-sm border-l-4 border-l-indigo-500">
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="font-bold text-slate-800 text-sm">{align.requiredWorkflow}</h4>
                                    <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs font-bold rounded uppercase">{align.priority}</span>
                                </div>
                                <p className="text-sm text-slate-600 mb-2"><span className="font-semibold text-slate-700">Ref:</span> {align.vulnerabilityRef}</p>
                                <p className="text-sm text-indigo-700 mt-2 bg-indigo-50 p-2 rounded"><strong>Action:</strong> {align.recommendedAction}</p>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>

            <Section title="Critical Asset Findings">
                <div className="overflow-x-auto bg-white rounded-lg border shadow-sm">
                    <table className="min-w-full text-sm text-left">
                        <thead className="bg-slate-100 text-slate-600 uppercase text-xs font-bold">
                            <tr>
                                <th className="px-4 py-3 border-b">Asset</th>
                                <th className="px-4 py-3 border-b">Type & Location</th>
                                <th className="px-4 py-3 border-b">Specific Risks</th>
                                <th className="px-4 py-3 border-b">Required Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {result.assetLevelFindings && result.assetLevelFindings.map((f, i) => (
                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-4 py-3 font-semibold text-slate-800">{f.assetName}</td>
                                    <td className="px-4 py-3 text-slate-600 truncate max-w-[150px]">{f.assetType} <br/><span className="text-xs text-slate-400">{f.location || ''}</span></td>
                                    <td className="px-4 py-3 text-red-700 text-xs">
                                        <ul className="list-disc pl-4 space-y-1">
                                            {f.specificRisks.map((r, j) => <li key={j}>{r}</li>)}
                                        </ul>
                                    </td>
                                    <td className="px-4 py-3 text-slate-700 italic border-l border-slate-100 bg-slate-50/50">{f.requiredAction}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>
            <Disclaimer text="Enterprise holistic aggregation powered by AI heuristics against geopolitical datastreams." />
        </div>
    );
};

export default EnterpriseFsatResultDisplay;
